// Client asset metadata only. Tileset regions retain the catalog's existing sprite names and game IDs.
export interface TilesetCell { sprite: string; column: number; row: number }
export interface TilesetDefinition {
  format: 'ashenheart-tileset'; version: 1; name: string; image: string; cellSize: 16;
  columns: number; rows: number; members: string[]; tiles: TilesetCell[];
}

export function parseTileset(value: unknown): TilesetDefinition {
  const object = (v: unknown, fields: string[]): Record<string, unknown> => {
    if (v === null || typeof v !== 'object' || Array.isArray(v) || Object.keys(v).some(k => !fields.includes(k))) throw new Error('Invalid tileset fields');
    return v as Record<string, unknown>;
  };
  const integer = (v: unknown, min: number, max: number): number => {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max) throw new Error('Invalid tileset dimension/cell');
    return v;
  };
  const sprite = (v: unknown): string => {
    if (typeof v !== 'string' || !/^Sprites\/[a-z0-9_-]+\.png$/.test(v)) throw new Error('Invalid tileset sprite path');
    return v;
  };
  const j = object(value, ['format', 'version', 'name', 'image', 'cellSize', 'columns', 'rows', 'members', 'tiles']);
  if (j.format !== 'ashenheart-tileset' || j.version !== 1 || j.cellSize !== 16) throw new Error('Unsupported tileset format');
  if (typeof j.name !== 'string' || j.name.length < 1 || j.name.length > 80 || /[^\x20-\x7e]/.test(j.name)) throw new Error('Invalid tileset name');
  if (typeof j.image !== 'string' || !/^[a-z0-9_-]+\.png$/.test(j.image)) throw new Error('Tileset image must be a local PNG filename');
  const columns = integer(j.columns, 1, 256), rows = integer(j.rows, 1, 256);
  if (!Array.isArray(j.tiles) || (j.members !== undefined && !Array.isArray(j.members))) throw new Error('Tileset needs tiles/member arrays');
  const names = new Set<string>(), cells = new Set<number>();
  const name = (v: unknown) => {
    const s = sprite(v); if (names.has(s)) throw new Error('Duplicate tileset sprite'); names.add(s); return s;
  };
  const members = (j.members as unknown[] | undefined ?? []).map(name);
  if (j.tiles.length === 0 && members.length === 0) throw new Error('Tileset needs tiles or members');
  const tiles = j.tiles.map(v => {
    const t = object(v, ['sprite', 'column', 'row']);
    const column = integer(t.column, 0, columns - 1), row = integer(t.row, 0, rows - 1), cell = row * columns + column;
    if (cells.has(cell)) throw new Error('Duplicate tileset cell'); cells.add(cell);
    return { sprite: name(t.sprite), column, row };
  });
  return { format: 'ashenheart-tileset', version: 1, cellSize: 16, name: j.name, image: j.image, columns, rows, members, tiles };
}
